import { useCallback, useEffect, useState } from 'react';
import { formatDateTime } from '../../util/datetime';
import { createPortal } from 'react-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import {
  getSystemStatus,
  getSystemStatusHistory,
  type ServiceStatus,
  type SystemStatusHistory,
  type SystemStatusSnapshot,
} from '../../api/systemStatus';
import { extractErrorMessage } from '../../api/errors';
import { RefreshButton } from '../../components/RefreshButton';
import { StatusHistoryStrip } from '../../components/StatusHistoryStrip';

/**
 * Superadmin System Status surface — styled after public status pages
 * (status.openai.com): a prominent overall banner, then one calm row
 * per component with a colored dot + word and a 7-day uptime strip.
 *
 * Polls `/api/v1/admin/system-status` every POLL_INTERVAL_MS so the
 * operator sees a fresh picture without hitting Refresh.
 */

const POLL_INTERVAL_MS = 15_000;

type PaletteKey = 'success' | 'warning' | 'error';

const STATUS_META: Record<
  ServiceStatus,
  { word: string; palette: PaletteKey }
> = {
  ok: { word: 'Operational', palette: 'success' },
  stale: { word: 'Degraded', palette: 'warning' },
  down: { word: 'Down', palette: 'error' },
};

const OVERALL_META: Record<
  ServiceStatus,
  { headline: string; palette: PaletteKey; icon: React.ReactNode }
> = {
  ok: {
    headline: 'All systems operational',
    palette: 'success',
    icon: <CheckCircleIcon sx={{ fontSize: 40 }} color="success" />,
  },
  stale: {
    headline: 'Partial degradation',
    palette: 'warning',
    icon: <WarningAmberIcon sx={{ fontSize: 40 }} color="warning" />,
  },
  down: {
    headline: 'Service outage',
    palette: 'error',
    icon: <ErrorIcon sx={{ fontSize: 40 }} color="error" />,
  },
};

// Column template for every component row: name/detail · uptime strip ·
// status. Shared so the strips line up down the card.
const ROW_GRID = 'minmax(0, 300px) minmax(0, 1fr) 168px';

/** One executive-summary figure in the overall banner. */
function BannerStat({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ textAlign: 'right' }}>
      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export function SystemStatus({
  embedded = false,
  actionsSlot = null,
}: { embedded?: boolean; actionsSlot?: HTMLElement | null } = {}) {
  const theme = useTheme();
  const [snapshot, setSnapshot] = useState<SystemStatusSnapshot | null>(null);
  const [history, setHistory] = useState<SystemStatusHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const reload = useCallback(async () => {
    setError(null);
    setRefreshing(true);
    try {
      const [snap, hist] = await Promise.all([
        getSystemStatus(),
        getSystemStatusHistory().catch(() => null),
      ]);
      setSnapshot(snap);
      if (hist) setHistory(hist);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to load system status'));
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    const id = window.setInterval(() => {
      void reload();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [reload]);

  const historyByName = (() => {
    const m = new Map<string, SystemStatusHistory['services'][number]>();
    if (history) for (const s of history.services) m.set(s.service_name, s);
    return m;
  })();

  if (error) {
    return (
      <Alert severity="error" onClose={() => setError(null)}>
        {error}
      </Alert>
    );
  }
  if (snapshot === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const checkedAt = formatDateTime(snapshot.checked_at);
  const overall: ServiceStatus = snapshot.services.some((s) => s.status === 'down')
    ? 'down'
    : snapshot.services.some((s) => s.status === 'stale')
      ? 'stale'
      : 'ok';
  const overallMeta = OVERALL_META[overall];

  // Executive summary — all derived from the snapshot + history we already
  // hold. Healthy share, mean response time, and uptime across the history
  // window (jobs/queue/error counts would need metrics we don't collect).
  const healthy = snapshot.services.filter((s) => s.status === 'ok').length;
  // Health-check round-trip latency. `response_ms` isn't comparable across
  // services: the API's own check is ~0 (self-check), a worker's is the age
  // since its last heartbeat (tens of seconds), and the dependency checks
  // (postgres, storage, …) are the real round-trips. So drop the worker
  // heartbeats and take the MEDIAN of the rest — robust to the 0 self-check
  // and to any single slow check.
  const latencyTimes = snapshot.services
    .filter(
      (s) => s.response_ms != null && !s.name.toLowerCase().includes('worker'),
    )
    .map((s) => s.response_ms as number)
    .sort((a, b) => a - b);
  const medianLatency = latencyTimes.length
    ? latencyTimes.length % 2 === 1
      ? latencyTimes[(latencyTimes.length - 1) / 2]
      : (latencyTimes[latencyTimes.length / 2 - 1] +
          latencyTimes[latencyTimes.length / 2]) /
        2
    : null;
  let okBuckets = 0;
  let totalBuckets = 0;
  if (history) {
    for (const svc of history.services) {
      for (const b of svc.buckets) {
        if (b.status != null) {
          totalBuckets += 1;
          if (b.status === 'ok') okBuckets += 1;
        }
      }
    }
  }
  const uptimePct = totalBuckets ? (okBuckets / totalBuckets) * 100 : null;

  const refresh = (
    <RefreshButton onClick={() => void reload()} refreshing={refreshing} />
  );

  return (
    <Stack spacing={3}>
      {actionsSlot ? (
        createPortal(refresh, actionsSlot)
      ) : (
        <Stack
          direction="row"
          justifyContent={embedded ? 'flex-end' : 'space-between'}
          alignItems="center"
        >
          {!embedded && <Typography variant="h4">System Status</Typography>}
          {refresh}
        </Stack>
      )}

      {/* Overall banner — the fastest read, tinted to the worst status. */}
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 2,
          borderLeft: `6px solid ${theme.palette[overallMeta.palette].main}`,
          bgcolor: alpha(theme.palette[overallMeta.palette].main, 0.08),
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {overallMeta.icon}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" fontWeight={700}>
            {overallMeta.headline}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last checked {checkedAt} · auto-refreshes every{' '}
            {POLL_INTERVAL_MS / 1000}s
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={{ xs: 2, sm: 3 }}
          sx={{ flexWrap: 'wrap', rowGap: 1 }}
        >
          <BannerStat label="Healthy" value={`${healthy}/${snapshot.services.length}`} />
          {medianLatency != null && (
            <BannerStat
              label="Median latency"
              value={`${Math.round(medianLatency)} ms`}
            />
          )}
          {uptimePct != null && (
            <BannerStat
              label={`${history?.window_hours ?? 168}h uptime`}
              value={`${uptimePct.toFixed(uptimePct >= 99.95 ? 2 : 1)}%`}
            />
          )}
        </Stack>
      </Paper>

      {/* Components — one calm row each, in a single dividered card. */}
      <Box>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ pl: 0.5 }}
        >
          Components
        </Typography>
        <Paper variant="outlined" sx={{ borderRadius: 2, mt: 0.5 }}>
          <Stack divider={<Divider />}>
            {snapshot.services.map((s) => (
              <ComponentRow
                key={s.name}
                service={s}
                history={historyByName.get(s.name)}
                bucketMinutes={history?.bucket_minutes ?? 60}
              />
            ))}
          </Stack>
        </Paper>
      </Box>
    </Stack>
  );
}

function ComponentRow({
  service: s,
  history,
  bucketMinutes,
}: {
  service: SystemStatusSnapshot['services'][number];
  history: SystemStatusHistory['services'][number] | undefined;
  bucketMinutes: number;
}) {
  const meta = STATUS_META[s.status];
  // Multi-replica worker rows come through as "name:instance_id".
  const [base, instance] = s.name.includes(':')
    ? s.name.split(':', 2)
    : [s.name, null];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: ROW_GRID,
        alignItems: 'center',
        columnGap: 2,
        px: 2,
        py: 1.5,
      }}
    >
      <Stack sx={{ overflow: 'hidden', minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
          <Typography variant="subtitle1" fontWeight={600} fontFamily="monospace">
            {base}
          </Typography>
          {instance && (
            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
              {instance}
            </Typography>
          )}
          {s.version && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
            >
              ({s.version})
            </Typography>
          )}
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {s.detail}
        </Typography>
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        {history && history.buckets.length > 0 && (
          <StatusHistoryStrip buckets={history.buckets} bucketMinutes={bucketMinutes} />
        )}
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
        {s.response_ms !== null && s.status !== 'down' && (
          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
            {formatResponseMs(s.response_ms, s.name)}
          </Typography>
        )}
        <FiberManualRecordIcon sx={{ fontSize: 12, color: `${meta.palette}.main` }} />
        <Typography variant="body2" fontWeight={600} sx={{ color: `${meta.palette}.main` }}>
          {meta.word}
        </Typography>
      </Stack>
    </Box>
  );
}

function formatResponseMs(ms: number, serviceName: string): string {
  // The worker's "response_ms" is the age of its last heartbeat, not a
  // round-trip — render it as a friendly duration; api/postgres render
  // as ms. The startsWith catches every "insights-worker:<id>" replica.
  if (serviceName.startsWith('insights-worker')) {
    if (ms < 1000) return `${ms.toFixed(0)} ms ago`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(0)} s ago`;
    if (ms < 3_600_000) return `${(ms / 60_000).toFixed(0)} min ago`;
    return `${(ms / 3_600_000).toFixed(1)} h ago`;
  }
  return `${ms.toFixed(1)} ms`;
}
